// Tolerant JSON extraction for AI responses.
// The model is instructed to return strict JSON, but we still defend against
// markdown fences, leading/trailing prose, and truncated payloads.

export class AIParseError extends Error {
  constructor(message: string, public readonly raw: string) {
    super(message);
    this.name = 'AIParseError';
  }
}

const FENCE = /```(?:json)?\s*([\s\S]*?)```/i;

function stripFences(text: string): string {
  const m = FENCE.exec(text);
  return m && m[1] ? m[1].trim() : text.trim();
}

function firstBalancedObject(text: string): string | null {
  const start = text.indexOf('{');
  if (start === -1) return null;
  let depth = 0;
  for (let i = start; i < text.length; i++) {
    const c = text[i];
    if (c === '{') depth++;
    else if (c === '}') {
      depth--;
      if (depth === 0) return text.slice(start, i + 1);
    }
  }
  return null;
}

function firstBalancedArray(text: string): string | null {
  const start = text.indexOf('[');
  if (start === -1) return null;
  let depth = 0;
  for (let i = start; i < text.length; i++) {
    const c = text[i];
    if (c === '[') depth++;
    else if (c === ']') {
      depth--;
      if (depth === 0) return text.slice(start, i + 1);
    }
  }
  return null;
}

export function parseAIObject<T>(raw: string): T {
  const cleaned = stripFences(raw);
  try {
    return JSON.parse(cleaned) as T;
  } catch {
    const fragment = firstBalancedObject(cleaned);
    if (fragment) {
      try {
        return JSON.parse(fragment) as T;
      } catch (err) {
        throw new AIParseError(`Could not parse object after recovery: ${(err as Error).message}`, raw);
      }
    }
    throw new AIParseError('No JSON object found in response', raw);
  }
}

export function parseAIArray<T>(raw: string): T[] {
  const cleaned = stripFences(raw);
  try {
    const parsed = JSON.parse(cleaned);
    return Array.isArray(parsed) ? (parsed as T[]) : [];
  } catch {
    const fragment = firstBalancedArray(cleaned);
    if (fragment) {
      try {
        const parsed = JSON.parse(fragment);
        return Array.isArray(parsed) ? (parsed as T[]) : [];
      } catch (err) {
        throw new AIParseError(`Could not parse array after recovery: ${(err as Error).message}`, raw);
      }
    }
    throw new AIParseError('No JSON array found in response', raw);
  }
}
